/**
 * "Click to open" and "submitting state" for the subscribe popup.
 *
 * Since 2026-09-17 the whole site keeps only one subscribe popup: sections/overlay-popup.liquid (the Pop-up in the admin Overlay group,
 * with data-popup). It is both the one that auto-opens after 8 seconds of browsing and the one opened by any
 * href="#SubscribePopup" button — the requester asked that "all subscribe popups use the same one".
 * sections/overlay-subscribe-popup.liquid is the old click-triggered version, disabled in the admin; if it's ever re-enabled,
 * the resolve below still prefers the Pop-up, so there won't be two popups each opening on their own.
 *
 * Event delegation instead of binding a listener to each button: the slideshow runs Flickity,
 * which clones slide nodes for infinite looping, and listeners on the clones aren't copied along.
 *
 * Doesn't touch open/close/ESC/clicking the overlay itself — all of that is handled by the
 * <modal-dialog> custom element in assets/modal-dialog.js.
 */

/** The modal-dialog on the page that serves as the "subscribe popup"; returns null if none is usable. */
function resolveSubscribeModal() {
	const candidates = document.querySelectorAll("modal-dialog[data-popup], #SubscribePopup");
	for (const modal of candidates) {
		// While the custom element hasn't finished upgrading, show doesn't exist; treat it as unusable (degrade to a plain anchor jump,
		// rather than intercepting the click and then silently failing).
		if (typeof modal.show === "function") return modal;
	}
	return null;
}

document.addEventListener("click", (event) => {
	const trigger = event.target.closest('a[href$="#SubscribePopup"]');
	if (!trigger) return;

	const modal = resolveSubscribeModal();
	if (!modal) return;

	event.preventDefault();
	modal.show(trigger);
});

/**
 * Submitting state: show a spinner on the button during the full-page POST (styles live in assets/overlay-group.css under the
 * .popup-modal .signup-container scope).
 *
 * Why it's needed: Shopify's customer form does a full-page POST and then redirects back to this page;
 * between the click and the new page rendering, the browser is waiting for the response with no feedback on the page; without feedback on the button
 * it's taken as "clicked and nothing happened" and clicked repeatedly.
 *
 * Why not the disabled attribute: the theme's .button[disabled] paints the background as
 * rgba(--color-body-rgb, .4) (semi-transparent gray), which on a solid black button looks like "grayed out" rather than
 * "loading"; also, a disabled submit button makes the full-page POST drop the submit button's name/value in some browsers.
 * This only does visuals, without changing submittability — duplicate submissions are deduplicated by Shopify itself.
 *
 * Event delegation bound on document (rather than binding once the popup node is in place): this script is
 * loaded with defer, and when it runs the popup DOM in overlay-group may not have been parsed yet —
 * delegation on document doesn't care about timing; by the time submit bubbles to the top, the form certainly exists.
 */
document.addEventListener("submit", (event) => {
	const form = event.target;
	if (!form || !form.classList || !form.classList.contains("signup-form")) return;
	// Only forms inside the subscribe popup: the footer / in-page banners also share the same .signup-form markup
	if (!form.closest("modal-dialog[data-popup], #SubscribePopup")) return;
	// Only for the text-button variant (the icon variant doesn't have this node)
	if (!form.querySelector(".signup-submit-loading")) return;
	form.classList.add("is-submitting");
});

/**
 * When restored from bfcache (the user pressed the browser's back button after submitting, or the page was cached whole),
 * the DOM still has the .is-submitting from last time, which shows as a spinner that never stops.
 * Clear the submitting state at the moment of restoration.
 */
window.addEventListener("pageshow", (event) => {
	if (!event.persisted) return;
	document
		.querySelectorAll("modal-dialog[data-popup] form.is-submitting, #SubscribePopup form.is-submitting")
		.forEach((form) => {
			form.classList.remove("is-submitting");
		});
});
